import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo
} from 'react';
import { useSelector } from 'react-redux';
import { CodeTableNumber } from '../interfaces';
import {
  UICustomizationValue,
  CustomizationValueConfig,
  FeatureToggleDataType
} from './featureToggle.constants';
import { useCodeTableMetadataDictionary } from '../api/reactQuery';
import { StorageService } from '../utility/StorageService';
import {
  getAPIFeatureToggleConfigs,
  getUICustomizationCustomValues,
  getUICustomizationFeatureToggles,
  getUIFeatureToggleConfigs,
  mergeFeatureToggleLayers
} from './featureToggle.helpers';
import { useTranslation } from '../hooks/useTranslation.hook';
import { UserFeatureToggleOverridesSelector } from '../security/session.state';
import {
  areFeatureOverridesEnabled,
  filterEligibleFeatureToggleOverrides,
  getActiveOverrideCount,
  isFeatureOverridesPanelAvailable
} from './featureOverrides.helpers';

interface FeatureToggleContextProps {
  features?: Partial<Record<FeatureToggleDataType, boolean>>;
  /** Code-table / BU config before user-preference and localStorage overlays. */
  baseFeatures?: Partial<Record<FeatureToggleDataType, boolean>>;
  customValues?: Partial<
    Record<UICustomizationValue, CustomizationValueConfig>
  >;
}

export const FeatureToggleContext = createContext<FeatureToggleContextProps>({
  features: {},
  baseFeatures: {},
  customValues: {}
});

export const FeatureToggleProvider = ({ children }: PropsWithChildren<{}>) => {
  const { t } = useTranslation();

  // A new instance every render would invalidate the merge memo below on every
  // render, re-deriving the whole toggle set and handing every consumer a new
  // context value.
  const storageService = useMemo(
    () =>
      new StorageService<FeatureToggleContextProps['features']>({
        namespace: 'FeatureToggle'
      }),
    []
  );

  const userFeatureToggleOverrides = useSelector(
    UserFeatureToggleOverridesSelector
  );

  const {
    data: codeTableFeatureToggles,
    isFetched: isCodeTableFeatureTogglesLoaded
  } = useCodeTableMetadataDictionary(CodeTableNumber.FeatureToggleConfig);

  const {
    data: codeTableBusinessUnitTemplate,
    isFetched: isCodeTableBusinessUnitTemplateLoaded
  } = useCodeTableMetadataDictionary(CodeTableNumber.BusinessUnitTemplate);

  /**
   * Merge API, UI, user-preference, and localStorage toggles in this order
   * (later wins for the same key):
   * 1. Server / BU config — code table 589 (API) then 470 (UI + customizations)
   * 2. Per-user Feature Overrides from preferences (local / edge / dbt / QA)
   * 3. Browser localStorage (`INV_FeatureToggle_features`)
   */
  const { customValues, baseFeatures, features, isFeatureTogglesLoaded } =
    useMemo(() => {
      const apiFeatureToggles = getAPIFeatureToggleConfigs(
        codeTableFeatureToggles
      );

      const uiFeatureToggles = getUIFeatureToggleConfigs(
        codeTableBusinessUnitTemplate
      );

      const uiCustomizationFeatureToggles = getUICustomizationFeatureToggles(
        codeTableBusinessUnitTemplate
      );

      const customValues = getUICustomizationCustomValues(
        codeTableBusinessUnitTemplate,
        t
      );

      const localFeatureToggles = storageService.retrieve('features');

      const baseFeatures = {
        ...apiFeatureToggles,
        ...uiFeatureToggles,
        ...uiCustomizationFeatureToggles
      };

      const eligibleUserOverrides = areFeatureOverridesEnabled()
        ? filterEligibleFeatureToggleOverrides(userFeatureToggleOverrides)
        : {};

      return {
        customValues,
        baseFeatures,
        features: mergeFeatureToggleLayers(
          baseFeatures,
          eligibleUserOverrides,
          localFeatureToggles
        ),
        isFeatureTogglesLoaded:
          isCodeTableFeatureTogglesLoaded &&
          isCodeTableBusinessUnitTemplateLoaded
      };
    }, [
      codeTableFeatureToggles,
      codeTableBusinessUnitTemplate,
      storageService,
      userFeatureToggleOverrides,
      t
    ]);

  return (
    <FeatureToggleContext.Provider
      value={{ features, baseFeatures, customValues }}
    >
      {!!isFeatureTogglesLoaded && children}
    </FeatureToggleContext.Provider>
  );
};

export function useFeatureToggleContext() {
  const { features, baseFeatures, customValues } =
    useContext(FeatureToggleContext);

  function isFeatureEnabled(feature: FeatureToggleDataType) {
    return !!features?.[feature];
  }

  /** Returns the code-table / BU config value before user-preference or localStorage overlays. */
  function isBaseFeatureEnabled(feature: FeatureToggleDataType) {
    return !!baseFeatures?.[feature];
  }

  function getCustomValue(key: UICustomizationValue) {
    return customValues?.[key]?.value;
  }

  return { isFeatureEnabled, isBaseFeatureEnabled, getCustomValue };
}

/**
 * Access helpers for the Feature Overrides panel and profile badge.
 * Uses the same eligibility + environment rules as FeatureToggleProvider merge.
 */
export function useFeatureOverridesAccess() {
  const userFeatureToggleOverrides = useSelector(
    UserFeatureToggleOverridesSelector
  );

  const panelAvailable = isFeatureOverridesPanelAvailable();

  const eligibleOverrides = areFeatureOverridesEnabled()
    ? filterEligibleFeatureToggleOverrides(userFeatureToggleOverrides)
    : {};

  const activeOverrideCount = getActiveOverrideCount(eligibleOverrides);

  return {
    panelAvailable,
    eligibleOverrides,
    activeOverrideCount,
    hasActiveOverrides: activeOverrideCount > 0
  };
}
